import { isTextboxField } from '@/domain/textbox/isTextboxField';
import { addField } from '@/infrastructure/engine2d/field/createFabricField';
import {
    addElementToLockedBackgroundObjectGroup,
    createLockedBackgroundObjectGroup,
} from '@/infrastructure/engine2d/helpers/FabricGroupHelper';
import { findFabricObjectById } from '@/infrastructure/engine2d/helpers/findFabricObjectById';
import { updateFontFamily } from '@/infrastructure/engine2d/text/textMutations';
import { applyImagePropertyToFabric } from '@/infrastructure/engine2d/scene/applyImagePropertyToFabric';
import { applyTextLayoutToFabric } from '@/infrastructure/engine2d/scene/applyTextLayoutToFabric';
import { applyTextStyleToFabric } from '@/infrastructure/engine2d/scene/applyTextStyleToFabric';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import type { TAddViewOptions } from '@/application/ports/TAddViewOptions';
import type { TImagePropertyPatch } from '@/domain/image/TImagePropertyPatch';
import type { TSelectionRange } from '@/domain/textbox/TSelectionRange';
import type { TTextLayoutPatch } from '@/domain/textbox/TTextLayoutPatch';
import type { TTextLayoutResult } from '@/domain/textbox/TTextLayoutResult';
import type { TTextStylePatch } from '@/domain/textbox/TTextStylePatch';
import type { TTextStyleResult } from '@/domain/textbox/TTextStyleResult';
import type { AField } from '@/domain/field/AField';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { IFabricTextCustom } from '@/infrastructure/engine2d/types/IFabricTextCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { Canvas, FabricImage } from 'fabric';

type TFabricSceneWriteServiceParams = {
    canvas: Canvas;
    scene: FabricScene;
};

// Every mutation the I2DEngine port exposes: entity views (add / update /
// remove / hydrate / setZOrder), entity patches (text / image), and
// canvas lifecycle (clear / init locked group / resize / parent bg).
// Reads live in FabricSceneQueryService, events in FabricPortEventsService.
class FabricSceneWriteService {
    private readonly _canvas: Canvas;
    private readonly _scene: FabricScene;

    constructor(params: TFabricSceneWriteServiceParams) {
        this._canvas = params.canvas;
        this._scene = params.scene;
    }

    // Fabric-per-kind dispatch lives in addField. Returns a Promise the
    // boot loop awaits; live callers can ignore it (fabric renders the
    // field as soon as the URL resolves). Selection is NOT this
    // method's concern — call setActiveEntity explicitly after add.
    async addView(entity: AField, options: TAddViewOptions = {}): Promise<void> {
        try {
            const fElem = await addField(entity, {
                addToCanvas: true,
                editable: options.editable,
                fabricWorldInstance: this._scene,
            });
            if (fElem && !fElem.selectable) {
                addElementToLockedBackgroundObjectGroup(this._canvas, LOCKED_FOREGROUND_GROUP_NAME, fElem);
                fElem.set('selectable', false);
            }
        } catch (error) {
            console.error('FabricSceneWriteService.addView failed:', error);
        }
    }

    async applyImageProperty(entityId: string, patch: TImagePropertyPatch): Promise<void> {
        const fImage = findFabricObjectById(this._canvas, entityId) as FabricImage | undefined;
        if (!fImage) {
            console.error(`FabricSceneWriteService.applyImageProperty: no fabric image for entity ${entityId}`);
            return;
        }
        await applyImagePropertyToFabric({ fImage, fabricCanvas: this._canvas, patch });
    }

    applyTextLayout(entityId: string, patch: TTextLayoutPatch): TTextLayoutResult | null {
        const fText = findFabricObjectById(this._canvas, entityId) as (IFabricTextCustom & ITextboxCustom) | undefined;
        if (!fText || (fText.type !== 'text' && fText.type !== 'textbox')) {
            return null;
        }
        return applyTextLayoutToFabric({ fText, fabricCanvas: this._canvas, patch });
    }

    applyTextStyle(entityId: string, patch: TTextStylePatch, range?: TSelectionRange): TTextStyleResult | null {
        const fText = findFabricObjectById(this._canvas, entityId) as (IFabricTextCustom & ITextboxCustom) | undefined;
        if (!fText || (fText.type !== 'text' && fText.type !== 'textbox')) {
            return null;
        }
        return applyTextStyleToFabric({ fText, fabricCanvas: this._canvas, patch, range });
    }

    // Safety-net for the font-loading race: even when initStore's
    // Promise.all waits on `_loadFonts` before mount, some browsers
    // may still be re-tokenising the just-added FontFace at the moment
    // `new Textbox(...)` runs, which measures via
    // CanvasRenderingContext2D.measureText using whichever font is
    // currently resolvable — a fallback bbox in the worst case.
    //
    // Re-applying the fontFamily via updateFontFamily({ initial: true })
    // captures the pre/post coords through initCenterElementOnItself
    // and re-centers if the bbox changed once the font is truly ready.
    // Local e2e (65/65 with all fonts cached) passes without this call,
    // but the local test fleet cannot exercise a fresh-load cold state.
    //
    // Split out of bulkHydrate as its own step so the responsibility is
    // explicit at the composition-root call-site (setupCanvasScene) and
    // this whole method becomes a candidate for removal the day the
    // race is proven impossible.
    refreshTextboxesAfterHydration(entities: AField[]): void {
        entities.forEach((entity) => {
            if (!isTextboxField(entity)) {
                return;
            }
            const fText = findFabricObjectById(this._canvas, entity.id) as (IFabricTextCustom & ITextboxCustom) | undefined;
            if (!fText || (fText.type !== 'text' && fText.type !== 'textbox')) {
                return;
            }
            updateFontFamily({
                fabricCanvas: this._canvas,
                fText,
                fontFamily: entity.fontFamily,
                initial: true,
            });
        });
    }

    initLockedBackgroundGroup(): void {
        createLockedBackgroundObjectGroup(this._canvas, LOCKED_FOREGROUND_GROUP_NAME);
    }

    removeView(entityId: string): void {
        this._scene.removeFabricElementById(entityId);
    }

    // Forces a single render — used to flush a batch that ran with
    // setAutoRender(false).
    renderNow(): void {
        this._canvas.renderAll();
    }

    resize(width: number, height: number): void {
        this._scene.resize(width, height);
    }

    // Fabric-side selection setter — pushes the ID from the store /
    // Vue call-site down to the canvas so the control handles show
    // up. Silent no-op if the fabric object hasn't materialised yet
    // (image URLs load async — the caller await'd addView, so at this
    // point the object exists).
    setActiveEntity(entityId: string | undefined): void {
        if (entityId === undefined) {
            this._canvas.discardActiveObject();
            this._canvas.requestRenderAll();
            return;
        }
        const fObject = findFabricObjectById(this._canvas, entityId);
        if (!fObject) {
            return;
        }
        this._canvas.setActiveObject(fObject);
        this._canvas.requestRenderAll();
    }

    // Toggle Fabric's built-in renderOnAddRemove — when false, add()
    // and remove() no longer schedule a render on their own, letting
    // a batch complete before we flush via renderNow().
    setAutoRender(enabled: boolean): void {
        this._canvas.renderOnAddRemove = enabled;
    }

    setZOrder(entityIds: string[]): void {
        // Sequential bringObjectToFront shifts only the entities — the locked
        // group, grid, graduation and mask keep their relative positions.
        entityIds.forEach((id) => {
            const object = findFabricObjectById(this._canvas, id);
            if (object) {
                this._canvas.bringObjectToFront(object);
            }
        });
        this._canvas.renderAll();
    }

    updateView(entity: AField): void {
        const object = this._scene.getFabricElementById(entity.id);
        if (!object) {
            return;
        }
        object.set({
            angle: entity.rotationDeg,
            left: entity.position.x,
            scaleX: entity.scale.x,
            scaleY: entity.scale.y,
            top: entity.position.y,
        });
        object.setCoords();
        this._canvas.renderAll();
    }
}

export { FabricSceneWriteService };
